import {BaseElement, IEltInitable, OSkinableInit} from "/@back@/commons/basis.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {IWedletModel, IWedSelector, IWedTheme, WED, WED_ROOT_SELECTOR, WedModel} from "/@back@/edit/wed/wedCore.js";
import {findScrollCtn, IElementWedlet, IRootWedlet, isSkAnnotListener, IWedlet, markAsScrollCtn, WEDLET} from "/@back@/edit/wed/wedlets/wedlet.js";
import {AccelKeyMgr, Action, IAccelKeyMgrPointer} from "/@lib@/commons/actions.js";
import {Desk} from "/@lib@/commons/desk.js";
import {EventsMgr, IEvents} from "/@lib@/commons/events.js";
import {ILastDatasBuilder, JLastDatas, LASTDATAS, OLastDatasInit} from "/@lib@/commons/lastDatas.js";
import {IReg, IRegPointer, IUiEnv, REG} from "/@lib@/commons/registry.js";
import {IView} from "/@lib@/commons/views.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {IJmlNode, IJmlSubSet} from "/@lib@/commons/xml/jml.js";
import {IXAddr, IXAddrRange, IXAddrRangeSel, XA} from "/@lib@/commons/xml/xAddr.js";
import {FetchAnnots, FetchJml, IDocFetch, IDocHolder, IDocHolderSync} from "/@lib@/edit/docHolder.js";
import {IMsgFilter, ListMsgOt, MsgOt} from "/@lib@/edit/ot/houseOt.js";
import {ErrorMsg, InitSlaveRep, Msg, ResetStatesMsg} from "/@lib@/edit/ot/urban.js";
import {isXmlMsg, XmlBatch, XmlDeleteMsg} from "/@lib@/edit/ot/xmlHouse.js";
import {isSkAnnotated} from "/@lib@/edit/schema/schema.js";
import {ISkAnnot} from "/@lib@/edit/schema/schemaAnnots.js";
import {ISkImporter, OSkImportDatas, OSkPasteContext, SkImpBase, SKMETA} from "/@lib@/edit/schema/schemaMeta.js";
import {ERROR} from "/@lib@/core/errorReport.js";
import {markdownParse} from "/@lib@/edit/import/markdown.js";
import {ThemeAction, UiThemeDeskFeat} from "/@back@/core/plugins/optionsPlg.js";
import {HistoEditPointsMgr, IHistoEditHolder} from "/@lib@/edit/histoEditPoints.js";
import {CLIPBOARD} from "/@lib@/commons/clipboard.js";

/**
 * Config du gestionnaire central d'un éditeur Wed.
 */
export interface OWedManagerConfig {
	/** Edition d'un élément : adresse de l'elt à éditer dans le docHolder. */
	xaRoot?: IXAddr

	/** Impose le modèle de wedlet racine de l'éditeur. */
	rootModel?: IWedletModel

	/** Si rootModel est nul, mode(s) pour rechercher le wedlet racine de l'éditeur. */
	startModes?: string[]

	/** Si rootModel est nul, Sélecteur personnalisé pour le wedlet racine de l'éditeur. */
	wedRootModelSelector?: IWedSelector

	/** Point d'insertion de l'éditor dans son rootNode. */
	insertRootWedletBefore?: Node

	/**
	 * Volume de contenu à rapatrier (l'unité correspond plus ou moins à 1 caractère / octet).
	 * Par défaut : pas de limite.
	 */
	fetchContentThreshold?: number

	/** Initialisation des amenant l'éditeur à être en readOnly. */
	readOnlyCauses?: string[]

	/** Désactive la suppression des balises inconnues / interdites. */
	noCleanup?: boolean

	/** Désactive toute captation de focus par les wedlets (preview...). */
	noFocus?: boolean

	/** Désactive la diffusion des annotations. */
	noAnnots?: boolean

	/**
	 * Filtre pour réduire les actions de undo/redo à un scope donné.
	 * Par défaut toutes les modifs issues de la ou des doors publics de la house sont pris en compte
	 * de la chaine des undo/redo (ie les msg des issues des autres houses sont sautées).
	 */
	undoRedoFilter?: IMsgFilter

	/**
	 * Gestion custom d'un DataTransfer (liens...)
	 */
	dataTransferAnalyzer?: IDataTransferWedAnalyzer

	/** Gestionnaire centralisé, inter-éditeurs, de l'historique des points d'édition. */
	histoEditPointsMgr?: HistoEditPointsMgr

	/** Détenteur de l'éditeur pour refocuser un point d'édition pour CET éditeur. */
	histoEditHolder?: IHistoEditHolder

	/** Surcharge le subReg instancié dans initWedMgr() combinant le reg du IWedEditor et du wedModel. */
	overrideReg?: (reg: IReg<any>) => void
}

/**
 * Events gérés par un wedMgr.
 */
export interface IWedMgrEvents extends IEvents {

	/** Processus de redraw de l'éditor : appelé avant le fetch au document. */
	redrawBeforeFetch(wedMgr: WedMgr, commands: IDocFetch[]): void

	/** Processus de redraw de l'éditor : appelé après le fetch, avant le bind des wedlets. */
	redrawBeforeBinds(wedMgr: WedMgr, commands: IDocFetch[]): void

	/** Processus de redraw de l'éditor : appelé en dernier, après tous les chargements asynchrones. */
	redrawAtEnd(wedMgr: WedMgr, commands: IDocFetch[]): void

	/** Appelé au début du traitement de chaque Msg reçu. */
	hookMsgBefore(wedMgr: WedMgr, msg: Msg): void

	/** Appelé à la fin du traitement de chaque Msg reçu. */
	hookMsgAfter(wedMgr: WedMgr, msg: Msg): void

	/**
	 * Appelé à la fin de la transaction avec la transaction complète et celle sensée être exploitée pour ajuster la sélection.
	 * Retourne true si ce hook a géré la sel, inutile d'appeler les hooks suivants.
	 */
	hookMsgForNextSel(wedMgr: WedMgr, transac: Msg, msgForSel: Msg | null): void | true

	/** Appelé à la fin d'une transaction. */
	hookEndTransac(wedMgr: WedMgr, transac: Msg): void

	/**
	 * Notification async après chaque modif, de la liste exhaustive des annotations.
	 * Si la deadline est dépassée la fonction peut juste se retourner elle-même,
	 * elle sera réappelée ultérieurement.
	 */
	asyncSkAnnots(wedMgr: WedMgr, annots: ISkAnnot[], deadline: RequestIdleDeadline): void | ((wedMgr: WedMgr, annots: ISkAnnot[], deadline: RequestIdleDeadline) => void)

	///** Appelé au début du traitement des SkAnnots reçus. */
	//hookSkAnnotBefore(wedMgr: WedMgr, removed: ISkAnnot[], added: ISkAnnot[]): void

	///** Appelé à la fin du traitement des SkAnnots reçus. */
	hookSkAnnotAfter(wedMgr: WedMgr, removed: ISkAnnot[], added: ISkAnnot[]): void


	/**
	 * Notifié lorsqu'un élément du wed capte le focus.
	 */
	getFocus(wedMgr: WedMgr, focused: Element): void

	looseFocus(wedMgr: WedMgr, event: FocusEvent): void

	/**
	 * Listener de changement de sélection (proxy de l'event html5 sur le document).
	 * Un listener qui a corrigé la sel doit retourner true: l'appel des listeners suivants sera interrompu.
	 * Il est donc important de placer les listeners susceptibles de corriger la sel en 1er, les autres en fin de liste.
	 *
	 * @param sel Objet Selection modifié ou null pour détecter une perte de sélection de l'éditeur.
	 */
	selection(wedMgr: WedMgr, sel: Selection | null): void | true

	/** handler des différents selMgr susceptibles de retourner la sel courante. Attention, le 1er qui retourne un range l'emporte. */
	getCurrentSel(wedMgr: WedMgr): IXAddrRange | undefined

	/** Notifié après une modification de l'état wedMgr.readOnly. */
	readOnlyChangeAfter(wedMgr: WedMgr, cause: string, readOnly: boolean, ...moreTuples: any[]): void

	/** Notifié après une changement du IWedEditorBarLayout. */
	layoutBarChange(wedMgr: WedMgr, bar: IWedEdBar): void

	/**
	 * Suppression définitive de l'éditor.
	 * Tous les traitements asynchrones planifiés doivent être tués.
	 */
	killEditor(wedMgr: WedMgr): void

	/** cf ILastDatasBuilder */
	buildLastDatas(wedMgr: WedMgr, lastDatas: JLastDatas): void

	/** */
	initLastDatas(wedMgr: WedMgr, lastDatas: JLastDatas): void

	/** cf IView.onViewShown(). */
	shown(wedMgr: WedMgr): void

	/** cf IView.onViewBeforeHide(). */
	beforeHide(wedMgr: WedMgr, close: boolean): boolean

	/** cf IView.onViewWaitForHide(). */
	beforeHideAsync(wedMgr: WedMgr, close: boolean): Promise<boolean>

	/** cf IView.onViewHidden(close?: boolean). */
	hidden(wedMgr: WedMgr, close: boolean): void

	/**
	 * Sur un event "paste", appelé uniquement si ev.clipboardData.files est spécifié.
	 * Le premier qui prend la main sur l'action doit retourner true: l'appel des listeners suivants est interrompu.
	 */
	pasteFiles(wedMgr: WedMgr, ev: ClipboardEvent, activeElt: Element): void
}

/**
 * Gestionnaire central d'un éditeur Wed.
 */
export class WedMgr implements ILastDatasBuilder {

	wedEditor: IWedEditor;
	wedModel: WedModel;

	/** Accès asynchrone au document. Toujours disponible. */
	docHolderAsync: IDocHolder;

	/** Accès synchrone au document. Peut-etre null si doc en read-only, mais obligatoire si document éditable. */
	docHolder: IDocHolderSync;

	config: OWedManagerConfig;

	/** undefined avant le 1er redraw, null si aucun wedlet racine trouvé. */
	rootWedlet: IRootWedlet;

	drawn: boolean;

	/**
	 * Le registre associé au wedMgr est un subReg de celui de l'editor ancré sur son rootNode et non le reg de l'editor lui-même.
	 * Raison : un IWedEditor peut contenir d'autres éditeurs dans des Bars (preview, outline, metas...) chacun disposant d'un wedMgr
	 * qui peuvent donc librement diverger de leur registre racine commun associé au IWedEditor.
	 * Note: IWedEditor.rootNode.reg est initialisé dans WedMgr.initWedMgr().
	 */
	get reg(): IReg<IUiEnv & any> {return this.wedEditor.rootNode.reg}

	accelKeyMgr: AccelKeyMgr<IWedEditor>;

	/**
	 * Indicateur de version courante du doc.
	 * Permet à des rafraichissements asynchrones de s'invalider si des modifs concurrentes ont eu lieu.
	 * XXX utile ?
	 */
	txStamp: number = 0;

	/**
	 * Peut geler la gestion auto du focus lors de l'exécution de msgs.
	 * Permet à des widgets d'édition de props de garder la main.
	 */
	freezeFocus: boolean;

	/**
	 * Mémoire du dernier élément focusé.
	 * l'activation d'un menu contextuel ou d'un bouton d'une toolbar fait perdre le focus
	 * dans l'éditeur. Cette prop permet de retrouver l'élément focusé.
	 */
	lastFocus: Element;

	/** Mémoire de l'event en cours pour bloquer la réentrance lié au delegate-host. */
	_lastFocusEvent: FocusEvent;

	/** */
	listeners: EventsMgr<IWedMgrEvents> = new EventsMgr();

	/**
	 * Panneau ouvert en position absolue qui ne respecte pas l'imbrication du DOM.
	 * Pour rechercher au cours du drag dans toutes les zones visibles.
	 */
	openedPaneWedlet?: IWedlet;

	/**
	 * Permet de mémoriser des données propres à un modèle et à un éditeur, mais mutualisés pour tous les widgets du même modèle.
	 * Retourne un dictionnaire sur lequel associer des datas via une key.
	 * La key model peut être un objet IWedletModel, un élément du template wed ou tout autre sous-objet unique.
	 */
	getDatasForModel(model: any): Dict<any> {
		if (!this._datasModels) this._datasModels = new Map();
		let datas = this._datasModels.get(model);
		if (!datas) {
			datas = {};
			this._datasModels.set(model, datas);
		}
		return datas;
	}

	protected _datasModels: Map<any, { [key: string]: any }>;

	/**
	 * Observe l'entrée de [target] dans la zone visible de son scroll container.
	 * Pour lazy construction.
	 * @return IntersectionObserver pour appeler unobserve(target).
	 */
	intersectObserve(target: IIntersectHandler): IntersectionObserver | null {
		const scroll = findScrollCtn(target);
		if (!scroll) return null;
		if (this._intersectObs == null) this._intersectObs = new Map();
		let obs = this._intersectObs.get(scroll);
		if (!obs) {
			obs = new IntersectionObserver(this.intersectObsCb, {root: scroll, rootMargin: "100%"});
			this._intersectObs.set(scroll, obs);
		}
		obs.observe(target);
		return obs;
	}

	private _intersectObs: Map<HTMLElement, IntersectionObserver>;

	constructor(wedEditor: IWedEditor) {
		this.wedEditor = wedEditor;
		this.accelKeyMgr = new AccelKeyMgr<IWedEditor>();
		this.wedEditor.addEventListener("keydown", function (this: IWedEditor, ev: KeyboardEvent) {
			this.wedMgr.accelKeyMgr.handleKeyboardEvent(ev, this);
		});
	}

	hookFct(name: keyof WedMgr, fct: Function, before?: boolean) {
		const overriden = this[name] as Function;
		if (before) {
			(this as any)[name] = () => {
				fct.apply(this, arguments);
				overriden.apply(this, arguments);
			}
		} else {
			(this as any)[name] = () => {
				overriden.apply(this, arguments);
				fct.apply(this, arguments);
			}
		}
	}


	get readOnly() {return this.docHolder == null || this.readOnlyCauses.size > 0}

	/**
	 * Ajoute ou supprime une ou plusieurs raisons justifiant le passage de l'éditeur en readOnly.
	 * Retourne true si l'état général de readOnly a été modifié, false sinon.
	 */
	setReadOnly(cause: string, readOnly: boolean, ...moreTuples: any[]): boolean {
		const oldSt = this.readOnly;
		if (readOnly) this.readOnlyCauses.add(cause);
		else this.readOnlyCauses.delete(cause);
		if (moreTuples) for (let i = 0; i < moreTuples.length; i = i + 2) {
			if (moreTuples[i + 1] as boolean) this.readOnlyCauses.add(moreTuples[i] as string);
			else this.readOnlyCauses.delete(moreTuples[i] as string);
		}
		if (oldSt !== this.readOnly) {
			//l'état a bien été modifié (on dispatch)
			WEDLET.refreshEditMode(this.rootWedlet);
			this.listeners.emit('readOnlyChangeAfter', this, cause, readOnly, ...moreTuples);
			return true;
		}
		return false;
	}

	/** Causes imposant le readOnly. NE PAS modifier de l'extérieur, voir .setReadOnly(cause, readOnly) */
	public readOnlyCauses = new Set<string>();

	trackWindowChanges() {
		document.addEventListener('selectionchange', this._selectionchangeLstn = this.onSelectionChange.bind(this));
		document.addEventListener('paste', this._pasteLstn = this.onPaste.bind(this));
		if (!this._themeChangeLstn && this.wedModel && this.wedModel.themes.length > 0 && desk && (desk as UiThemeDeskFeat).onThemeChange) {
			this._themeChangeLstn = this.refreshTheme.bind(this);
			(desk as UiThemeDeskFeat).onThemeChange.add(this._themeChangeLstn);
		}
	}

	untrackWindowChanges() {
		document.removeEventListener('selectionchange', this._selectionchangeLstn);
		document.removeEventListener('paste', this._pasteLstn);
		if (this._themeChangeLstn) {
			(desk as UiThemeDeskFeat).onThemeChange.delete(this._themeChangeLstn);
			this._themeChangeLstn = null;
		}
	}

	protected _selectionchangeLstn: EventListenerOrEventListenerObject;
	protected _pasteLstn: (ev: ClipboardEvent) => void;
	protected _themeChangeLstn: (theme: ThemeAction) => void;


	initWedMgr(wedModel: WedModel, docHolder: IDocHolder, config?: OWedManagerConfig): Promise<boolean> {
		docHolder.setSubRoot(config?.xaRoot);
		const rootNode = this.wedEditor.rootNode;
		if (!this.config) {
			//1er init, abonnements sur wedEditor.rootNode.
			(rootNode as IWedMgrPointer).wedMgr = this;
			rootNode.addEventListener('focus', onFocus, true);
			rootNode.addEventListener('blur', onBlur, true);
			rootNode.addEventListener('delegate-host', (ev: Event) => {
				const elt = ev.composedPath()[0];
				const host = (elt as IElementWedlet).delegatedHost;
				(host as any as IWedMgrPointer).wedMgr = this;
				host.addEventListener('focus', onFocus, true);
				host.addEventListener('blur', onBlur, true);
			});
		}
		if (this.docHolderAsync) this.detachDocHolder();
		this.config = config || {};
		this.wedModel = wedModel;

		this.readOnlyCauses.clear();
		if (this.config.readOnlyCauses) for (const c of this.config.readOnlyCauses) this.readOnlyCauses.add(c);

		if (!this._themeChangeLstn && this.wedModel.themes.length > 0 && desk && (desk as UiThemeDeskFeat).onThemeChange) {
			this._themeChangeLstn = this.refreshTheme.bind(this);
			(desk as UiThemeDeskFeat).onThemeChange.add(this._themeChangeLstn);
		}

		rootNode.reg = REG.createSubReg(this.wedEditor, wedModel.reg);
		if (config.overrideReg) config.overrideReg(rootNode.reg)
		if (wedModel.themes.length > 0) this.refreshTheme(desk && (desk as UiThemeDeskFeat).currentTheme);
		this.docHolderAsync = docHolder;
		if (docHolder.isDocHolderSync()) {
			this.docHolder = docHolder;
			this.docHolder.noCleanup = this.config.noCleanup || false;
		}
		return this.redrawEditor(function (this: WedMgr) {
			this.docHolderAsync.addMsgListener(this._msgLstn = (msg: Msg) => {
				if (this.rootWedlet === undefined) return; //peut arriver si redraw avec un sub-editor (outline).
				try {
					this.executeMsg(msg);
				} catch (e) {
					ERROR.log("Exec msg failed, editor will be redrawn.", e);
					this.redrawEditor();
				}
			});
			this.docHolder?.houseLstn.on("onSkAnnotsChange", this._onSkAnnots || (this._onSkAnnots = this.onSkAnnots.bind(this)));
		});
	}

	/** Retourne un module JS déclaré dans le WED. */
	getJsModule<M>(key: string): M | null {
		return this.wedModel?.jsLibs[key] as M | null;
	}

	_currentTheme: IWedTheme;

	refreshTheme(theme: ThemeAction): void {
		const ctxTheme = theme ? theme.getId() : "";
		const st = this.wedEditor.rootNode.style;
		for (const th of this.wedModel.themes) {
			if (!th.themeContext || ctxTheme.match(th.themeContext)) {
				if (this._currentTheme) for (let n of Object.getOwnPropertyNames(this._currentTheme.themeVars)) st.removeProperty(n);
				this._currentTheme = th;
				for (let n of Object.getOwnPropertyNames(th.themeVars)) st.setProperty(n, th.themeVars[n]);
				break;
			}
		}
	}

	protected _msgLstn: (msg: Msg) => void;

	/**
	 * Suppression définitive de cet éditeur.
	 */
	killEditor() {
		this.detachDocHolder();
		if (this.asyncSkAnnotsTask) window.cancelIdleCallback(this.asyncSkAnnotsTask);
		if (this.asyncSkAnnotsRecallTask) window.cancelIdleCallback(this.asyncSkAnnotsRecallTask);
		this.listeners.emit("killEditor", this);
	}

	/**
	 * Détache cet editor de son docHolder et efface son contenu.
	 * Cet éditeur peut encore être utilisé après cet appel via this.initWedMgr()
	 */
	detachDocHolder() {
		if (this.docHolderAsync && this.docHolderAsync.isAvailable) {
			this.docHolderAsync.removeMsgListener(this._msgLstn);
			if (this._asyncSkAnnots) this.docHolder.houseLstn.removeListener("onSkAnnotsChange", this._onSkAnnots);
			this.docHolderAsync.close();
			this.docHolderAsync = null;
			this.docHolder = null;
		}
		this.clearEditor();
	}


	/** Reset cet editor, sans le détacher de son docHolder. */
	clearEditor() {
		this.drawn = false;
		this.wedEditor.clearContent();
		this.rootWedlet = undefined; //undefined = état non init, null = état pas trouvé de root.
		if (this._intersectObs) {
			this._intersectObs.forEach((i) => i.disconnect());
			this._intersectObs = null;
		}
	}

	protected _lastSelChanged: Selection = null;

	protected onSelectionChange() {
		if (this.freezeFocus) return;
		const activeElt = DOMSH.findDeepActiveElement() as HTMLElement;
		if (!DOMSH.isFlatAncestor(this.wedEditor.rootNode, activeElt)) {
			if (DOMSH.isFlatAncestor(this.wedEditor, activeElt)) return; //edition de champs dans les barres de l'éditeur.
			//sel en dehors de cet éditor.
			if (this._lastSelChanged !== null) {
				this._lastSelChanged = null;
				this.listeners.emitUntil("selection", this, null);
			}
			return;
		}
		const sel = this._lastSelChanged = DOMSH.findDocumentOrShadowRoot(activeElt).getSelection();
		//console.log("onSelChange::", sel.type, sel.focusNode, sel.focusOffset, sel.anchorNode, sel.anchorOffset, sel.rangeCount);
		this.listeners.emitUntil("selection", this, sel);
	}

	protected onPaste(ev: ClipboardEvent) {
		const from = DOMSH.findDeepActiveElement();
		if (DOM.isLogicalAncestor(this.wedEditor.rootNode, from)) {
			if (ev.clipboardData.files?.length > 0) {
				//Coller de fichiers externes.
				const activeElt = DOMSH.findDeepActiveElement();
				if (!DOMSH.isFlatAncestor(this.wedEditor.rootNode, activeElt)) {
					//pas dans cet éditeur.
					return;
				}
				//On bloque toutes les actions standard dans l'éditeur.
				ev.preventDefault();
				this.listeners.emitUntil("pasteFiles", this, ev, activeElt);
			} else {
				//On bloque toutes les actions standard dans l'éditeur.
				ev.preventDefault();
			}
		}
	}

	getCurrentSel(): IXAddrRangeSel | undefined {
		return this.listeners.emitUntil("getCurrentSel", this);
	}

	protected intersectObsCb(this: void, entries: IntersectionObserverEntry[], observer: IntersectionObserver) {
		for (let i = 0, s = entries.length; i < s; i++) {
			(entries[i].target as IIntersectHandler).onIntersectChange(entries[i]);
		}
	}

	fetchContent(from: IXAddr): Promise<FetchJml> {
		return this.docHolderAsync.fetchContent([new FetchJml(from, 1, this.config.fetchContentThreshold)]).then((f: FetchJml[]) => f[0]);
	}

	/**
	 * ATTENTION: Utilisable uniquement si le docHolder est un IDocHolderSync.
	 */
	getContent(from: IXAddr): IJmlSubSet {
		return this.config.fetchContentThreshold > 0 ? this.docHolder.getContentThreshold(this.config.fetchContentThreshold, from) : this.docHolder.getContent(from);
	}

	/**
	 * Si un wedlet est reconstruit, il peut demander à recevoir à nouveau les différentes annotations (schéma, diff, collab, comment...)
	 * le concernant lui et sa descendance.
	 * XXX Si rebuild dans batch, passer en doAfterBatch() si modfs après ? mais besoin d'ajuster xaFrom aussi ? ou doAfterBatch avec ref wedlet ?
	 * ATTENTION: Utilisable uniquement si le docHolder est un IDocHolderSync.
	 */
	dispatchAnnotsAfterRebuildWedlet(xaFrom: IXAddr) {
		if (this.config.noAnnots) return;
		if (this.docHolder && this.rootWedlet) {
			this.dispatchAddedSkAnnots(this.docHolder.getAnnots(xaFrom));
		} else {
			console.trace("TODO dispatchAnnotsAfterRedraw async");
		}
	}

	redrawEditor(execInSyncAfterRedraw?: (this: WedMgr) => void): Promise<boolean> {
		this.drawn = false;
		this._doAfterBatch = null;
		const xaRoot = this.config.xaRoot;  //FIXME config.xaRoot obsolet ? => this.docHolderAsync.getSubRoot() ?
		const commands = [new FetchJml(xaRoot, 1, this.config.fetchContentThreshold), new FetchAnnots(xaRoot)];
		this.listeners.emit("redrawBeforeFetch", this, commands);
		this.clearEditor();
		return this.docHolderAsync.fetchContent(commands).then(async (cmds: IDocFetch[]) => {
			this.txStamp++;
			this.listeners.emit("redrawBeforeBinds", this, cmds);
			const jml = (cmds[0] as FetchJml).result;
			const docModel = this.config.rootModel || this.wedModel.findModelForDocument(this.config.startModes || WED.DEFAULT_MODES, this, WED.combineSelectors(this.config.wedRootModelSelector, WED_ROOT_SELECTOR)); //TODO this.config.wedRootParams ?
			this.rootWedlet = docModel ? docModel.createRootWedlet(this.wedEditor.mainContainer, this.config.insertRootWedletBefore, this) : null;
			let promise;
			if (xaRoot) {
				const rootNode = jml[0] as IJmlNode;
				if (!rootNode) {
					this.wedEditor.dispatchEvent(new CustomEvent('wed-editor-root-deleted'));
					this.detachDocHolder();
					return false;
				}
				if (this.rootWedlet) promise = this.rootWedlet.bindRoot((jml[1] || []) as IJmlSubSet, rootNode, xaRoot);
			} else {
				if (this.rootWedlet) promise = this.rootWedlet.bindRoot(jml);
			}
			if (execInSyncAfterRedraw) execInSyncAfterRedraw.call(this);
			if (this._doAfterBatch) {
				for (let i = 0; i < this._doAfterBatch.length; i++) this._doAfterBatch[i]();
				this._doAfterBatch = null;
			}
			const annots = (cmds[1] as FetchAnnots).result;
			if (annots && annots.length > 0) this.dispatchSkAnnots(null, annots);
			if (promise) await promise;
			this.listeners.emit("redrawAtEnd", this, cmds);
			this.drawn = true;
			return this.rootWedlet != null;
		});
	}

	/**
	 * Au cours d'un batch (ou d'un redraw), ajoute un traitement après son traitement complet.
	 * Obligatoire pour les traitements qui ont besoin de réinterroger la house
	 * (il faut attendre que la house et l'ihm soient à nouveau synchro pour
	 * ne pas avoir de décalage d'offsets).
	 */
	doAfterBatch(cb: () => void) {
		if (this.currentMsgsStack.length > 0 || !this.drawn) {
			//on est dans un batch d'update.
			if (!this._doAfterBatch) this._doAfterBatch = [cb];
			else this._doAfterBatch.push(cb);
		} else {
			cb();
		}
	}

	protected _doAfterBatch: (() => void)[];

	/** Prochain message qui devrait marquer la sélection courante ou le focus. */
	msgForNextSel: Msg;

	/** Pile de Msg en cours de traitement. */
	currentMsgsStack: Msg[] = [];

	get currentMsg(): Msg | null {return this.currentMsgsStack[this.currentMsgsStack.length - 1]}

	isUndoRedoPending(m: Msg): boolean {
		if (m.metas && (m.metas.undo || m.metas.redo)) return true;
		if (m instanceof ListMsgOt) {
			for (let i = 0; i < m.msgs.length; i++) {
				if (this.isUndoRedoPending(m.msgs[i])) return true;
			}
		}
		return false;
	}

	protected executeMsg(msg: Msg) {
		this.currentMsgsStack.push(msg);
		try {
			this.listeners.emit("hookMsgBefore", this, msg);
			if (isXmlMsg(msg)) {
				//XmlInsertMsg, XmlDeleteMsg, XmlStrMsg
				this.txStamp++;
				WEDLET.applyUpdate(this.rootWedlet, msg);
			} else if (msg instanceof ListMsgOt) {
				for (let i = 0; i < msg.msgs.length; i++) this.executeMsg(msg.msgs[i]);
			} else if (msg.type === ResetStatesMsg.type || msg.type === InitSlaveRep.type) {
				//console.log("redrawEditor after:::", msg);
				this.redrawEditor(function (this: WedMgr) {
					this.listeners.emit("hookMsgAfter", this, msg);
				});
				return;
			} else if (msg instanceof ErrorMsg) {
				//console.log(msg);
				switch (this.wedEditor.onMsgError(msg)) {
				case 'redraw':
					this.redrawEditor(function (this: WedMgr) {
						this.listeners.emit("hookMsgAfter", this, msg);
					});
					break;
				}
				return;
			}
			if (!this.rootWedlet) return; //editor supprimé ('wed-editor-root-deleted').
			this.listeners.emit("hookMsgAfter", this, msg);
			if (isSkAnnotated(msg)) this.dispatchSkAnnots(msg.skAnnotsToRemove, msg.skAnnotsToAdd);
			else if (containsDeleteMsg(msg)) {
				//Une suppr de noeuds peut contenir des annots dans la descendance => besoin de refresh systématique.
				this.dispatchSkAnnots(null, null);
			}
		} finally {
			this.currentMsgsStack.pop();
		}
		if (this.currentMsgsStack.length === 0) {
			//on est à la fin de la transaction.
			if (this._doAfterBatch) {
				for (let i = 0; i < this._doAfterBatch.length; i++) {
					this._doAfterBatch[i]();
				}
				this._doAfterBatch = null;
			}
			if (!this.freezeFocus) {
				try {
					this.listeners.emitUntil("hookMsgForNextSel", this, msg, this.findAndAdjustMsgForNextSel(msg));
				} catch (e) {
					ERROR.log("hookMsgForNextSel failed", e, {reg: this.reg, msg});
				}
				//fixup de la selection après update (pas d'event "selectionchange", mais sel potentiellement déplacée)
				this.onSelectionChange();
			}
			this.listeners.emit("hookEndTransac", this, msg);
		}
	}

	protected findAndAdjustMsgForNextSel(msg: Msg): Msg {
		if (msg instanceof XmlBatch && msg.selAfter != null) {
			return msg;
		} else if (isXmlMsg(msg)) {
			return msg;
		} else if (msg instanceof ListMsgOt) {
			//on désactive le focus sur tous les msgs d'autoCorrects.
			if (msg.getMeta("autoCorrect")) return null;
			for (let i = msg.msgs.length - 1; i >= 0; i--) {
				const m = msg.msgs[i];
				if (!m.getMeta("autoCorrect")) {
					//on est le dernier msg avant autoCorrect.
					let res = this.findAndAdjustMsgForNextSel(m);
					if (res && res instanceof MsgOt && i < msg.msgs.length - 1) {
						res = res.clone();
						for (let j = i + 1; j < msg.msgs.length; j++) {
							const n = msg.msgs[j];
							if (n instanceof MsgOt) n.adjust(res as MsgOt);
						}
					}
					return res;
				}
			}
		}
	}

	protected _onSkAnnots: (removed: ISkAnnot[] | null, added: ISkAnnot[] | null) => void;

	onSkAnnots(removed: ISkAnnot[] | null, added: ISkAnnot[] | null) {
		//Revalid externe aux msgs en cours de traitement (ptrItem...).
		if (this.currentMsgsStack.length === 0) this.dispatchSkAnnots(removed, added);
	}

	dispatchSkAnnots(removed: ISkAnnot[] | null, added: ISkAnnot[] | null) {
		//console.log("dispatchSkAnnots...", removed, added);
		if (this.config.noAnnots) return;
		//this.listeners.emit("hookSkAnnotBefore", this, removed, added);
		//console.log("removed annots:::", removed, "added annots::", added);
		const root = this.rootWedlet;
		if (root && removed) {
			for (let i = 0, s = removed.length; i < s; i++) {
				const annot = removed[i];
				const xa = annot.anchor || annot.start;
				if (xa) {//annot non killed
					//optimisation (search en partuclier : N annots sur le même noeud)
					let k = i + 1;
					while (k < s && removed[k].anchorNode === annot.anchorNode) k++;
					WEDLET.findWedlets(root, xa, WEDLET.FINDOPTIONS_lastAncestorIfNone).forEach((wedlet: IWedlet) => {
						if (isSkAnnotListener(wedlet)) {
							wedlet.onRemovedSkAnnot(annot, xa);
							if (i < k) for (let m = i + 1; m < k; m++) wedlet.onRemovedSkAnnot(removed[m], xa);
						}
					});
					i = k - 1;
				}
			}
		}
		if (root && added) this.dispatchAddedSkAnnots(added);
		this.listeners.emit("hookSkAnnotAfter", this, removed, added);

		this._lastSkAnnots = null; //Reset des lastSkAnnots invalides.
		if (this.listeners.hasListeners("asyncSkAnnots")) {
			if (!this.asyncSkAnnotsTask) {
				if (this.asyncSkAnnotsRecallTask) window.cancelIdleCallback(this.asyncSkAnnotsRecallTask);
				this.asyncSkAnnotsTask = window.requestIdleCallback((this._asyncSkAnnots || (this._asyncSkAnnots = this.asyncSkAnnots.bind(this))));
			}
		}
	}

	protected dispatchAddedSkAnnots(added: ISkAnnot[]) {
		for (let i = 0, s = added.length; i < s; i++) {
			const annot = added[i];
			const xa = annot.anchor || annot.start;
			//optimisation (search en partuclier : N annots sur le même noeud)
			let k = i + 1;
			while (k < s && added[k].anchorNode === annot.anchorNode) k++;
			WEDLET.findWedlets(this.rootWedlet, xa, WEDLET.FINDOPTIONS_lastAncestorIfNone).forEach((wedlet: IWedlet) => {
				if (isSkAnnotListener(wedlet)) {
					wedlet.onAddedSkAnnot(annot, xa);
					if (i < k) for (let m = i + 1; m < k; m++) wedlet.onAddedSkAnnot(added[m], xa);
				}
			});
			i = k - 1;
		}
	}

	get lastSkAnnots(): ISkAnnot[] {
		if (!this._lastSkAnnots) this._lastSkAnnots = this.docHolder.getAnnots(this.rootWedlet.wedAnchor);
		return this._lastSkAnnots;
	}

	protected _lastSkAnnots: ISkAnnot[];
	protected asyncSkAnnotsTask?: number;
	protected asyncSkAnnotsRecallTask?: number;
	protected _asyncSkAnnots?: (deadline: RequestIdleDeadline) => void;

	asyncSkAnnots(deadline: RequestIdleDeadline) {
		this.asyncSkAnnotsTask = 0;
		const toRecall = this.listeners.emitUntil("asyncSkAnnots", this, this.lastSkAnnots, deadline);
		if (toRecall) this.asyncSkAnnotsRecall(toRecall);
	}

	asyncSkAnnotsRecall(lstnToRecall: any) {
		this.asyncSkAnnotsRecallTask = window.requestIdleCallback((deadline: RequestIdleDeadline) => {
			this.asyncSkAnnotsRecallTask = 0;
			const toRecall = this.listeners.emitUntilAfter("asyncSkAnnots", lstnToRecall, true, this, this.lastSkAnnots, deadline);
			if (toRecall) this.asyncSkAnnotsRecall(toRecall);
		});
	}

	/**
	 * Copie un range dans le presse-papier.
	 */
	async writeRangeToClipboard(range: IXAddrRange, dataTrsf?: DataTransfer): Promise<void> {
		if (!XA.isCollapsed(range) || XA.isAttribute(range.start)) {
			const doc = this.docHolder;
			if (doc) {
				const frag = doc.exportRange(range, 'multi', {'application/xml': null, 'text/plain': null, 'text/html': null});
				//console.log("COPY::::::", frag);
				if (CLIPBOARD.hasCustomType) {
					//Si api async clipboard, il faut privilégier cette api, sinon un paste via menu ne pourra pas extraire le format xml.
					return CLIPBOARD.writeInClipboard(() => navigator.clipboard.write([new ClipboardItem({
						"text/plain": new Blob([frag['text/plain']], {type: "text/plain"}),
						"text/html": new Blob([frag['text/html']], {type: "text/html"}),
						"web application/xml": new Blob([frag['application/xml']], {type: "application/xml"})
					})]), this.wedEditor);
				} else if (dataTrsf) {
					dataTrsf.setData('application/xml', frag['application/xml']);
					dataTrsf.setData('text/plain', frag['text/plain']);
					dataTrsf.setData('text/html', frag['text/html']);
				}
			}
		}
	}

	/** Copie en text/plain la représentation xml. */
	async writeRangeToClipboardAsXml(range: IXAddrRange): Promise<void> {
		if (!XA.isCollapsed(range)) {
			const doc = this.docHolder;
			if (doc) return CLIPBOARD.writeInClipboard(() => navigator.clipboard.writeText(doc.exportRange(range, 'application/xml')), this.wedEditor);
		}
	}

	/**
	 * Copie un ensemble de noeuds dans le presse-papier.
	 */
	async writeNodesToClipboard(xaNodes: IXAddr[], filters: Jsonisable[], dataTrsf?: DataTransfer): Promise<void> {
		const doc = this.docHolder;
		if (doc) {
			const frag = doc.exportNodes(xaNodes, filters, 'multi', {'application/xml': null, 'text/plain': null, 'text/html': null});
			if (CLIPBOARD.hasCustomType) {
				//Si api async clipboard, il faut privilégier cette api, sinon un paste via menu ne pourra pas extraire le format xml.
				return CLIPBOARD.writeInClipboard(() => navigator.clipboard.write([new ClipboardItem({
					"text/plain": new Blob([frag['text/plain']], {type: "text/plain"}),
					"text/html": new Blob([frag['text/html']], {type: "text/html"}),
					"web application/xml": new Blob([frag['application/xml']], {type: "application/xml"})
				})]), this.wedEditor);
			} else if (dataTrsf) {
				//console.log("COPY::::::", frag);
				dataTrsf.setData('text/plain', frag['text/plain']);
				dataTrsf.setData('text/html', frag['text/html']);
				dataTrsf.setData('application/xml', frag['application/xml']);
			}
		}
	}

	async writeNodesToClipboardAsXml(xaNodes: IXAddr[], filters: Jsonisable[]): Promise<void> {
		const doc = this.docHolder;
		if (doc) return CLIPBOARD.writeInClipboard(() => navigator.clipboard.writeText(doc.exportNodes(xaNodes, filters, 'application/xml')), this.wedEditor);
	}

	/**
	 * Recherche les possibilités d'import issu du clipboard pour une action de type "Coller ici".
	 */
	async tryPaste(context: OSkPasteContext, cache: OSkImportDatas, clipboardItems: ClipboardItem[]): Promise<ISkImporter[]> {
		const doc = this.docHolder;
		if (doc && !this.readOnly) {
			if (this.config.dataTransferAnalyzer) {
				const r = await this.config.dataTransferAnalyzer.tryPaste(clipboardItems, context, cache, this);
				if (r) return SkImpBase.filterAcceptables(r);
			}
			return this.tryPasteDefault(context, cache, clipboardItems[0]);
		}
		return null;
	}


	/**
	 * Recherche les possibilités d'import issu de fichiers trsfFiles.files.
	 */
	async tryPasteFiles(context: OSkPasteContext, cache: OSkImportDatas, trsfFiles: DataTransfer): Promise<ISkImporter[]> {
		if (this.config.dataTransferAnalyzer && !this.readOnly && this.docHolder) {
			const r = await this.config.dataTransferAnalyzer.tryPasteFiles(trsfFiles, context, cache, this);
			if (r) return SkImpBase.filterAcceptables(r);
		}
		return null;
	}

	/** Traitement isolé de tryPaste pour pouvoir être appelé de IDataTransferWedAnalyzer. */
	async tryPasteDefault(context: OSkPasteContext, cache: OSkImportDatas, clipboard: ClipboardItem): Promise<ISkImporter[]> {
		const doc = this.docHolder;
		if (!context.targetHint) {
			const xmlType = clipboard.types.find((t) => t === 'application/xml' || t === 'web application/xml');
			if (xmlType) {
				const content = DOM.parseDom(await CLIPBOARD.text(clipboard, xmlType));
				DOM.cleanupDom(content, true, false, true);
				cache.originalDom = content.documentElement;
				const htmlFallback = clipboard.types.indexOf('text/html') >= 0 ? await CLIPBOARD.text(clipboard, 'text/html') : null;
				const imp = await doc.house.schemaDom.tryPasteNodes(context, content.documentElement, cache);
				if (htmlFallback && imp?.reduce((acc, imp) => Math.min(acc, imp.malus), Infinity) > 0) {
					//PAs de solution parfaite, on tente en fallbackHtml
					const docFrag = DOM.parseDomValid(htmlFallback, null, "text/html");
					//console.log("HTML:::", datas, docFrag);
					if (docFrag) {
						cache.originalHtml = docFrag.body;
						const imp2 = await doc.house.schemaDom.tryPasteNodes(context, docFrag.body, cache);
						if (imp2) imp.push(...SKMETA.replaceLabelOnImports(imp2, "➲ Collage approximatif"));
					}
				}
				return SkImpBase.filterAcceptables(imp);
			}
			if (clipboard.types.indexOf('text/html') >= 0) {
				try {
					const docFrag = DOM.parseDomValid(await CLIPBOARD.text(clipboard, 'text/html'), null, "text/html");
					//console.log("HTML:::", datas, docFrag);
					if (docFrag) {
						cache.originalHtml = docFrag.body;
						const imp = await SkImpBase.filterAcceptables(await doc.house.schemaDom.tryPasteNodes(context, docFrag.body, cache));
						if (imp) return SKMETA.replaceLabelOnImports(imp, "➲ Import HTML");
					}
					// const docFrag = new Range().createContextualFragment(dataTrsf.getData('text/html'));
					// if (docFrag.hasChildNodes()) {
					// 	cache.originalDom = docFrag;
					// 	let imp = await doc.house.schemaDom.tryPasteNodes(context, docFrag, cache);
					// 	if (imp) return imp;
					// }
					return null;
				} catch (e) {}
			}
		}
		if (clipboard.types.indexOf('text/plain') >= 0) {
			const text = await CLIPBOARD.text(clipboard, 'text/plain');
			if (text) {
				cache.originalText = text;
				if (context.targetHint !== "string") {
					await this.tryParseAsNodeAsync(text, cache, context.targetHint)
					if (cache.originalDom || cache.originalHtml) {
						return SkImpBase.filterAcceptables(await doc.house.schemaDom.tryPasteNodes(context, cache.originalDom || cache.originalHtml, cache));
					}
				}
				return SkImpBase.filterAcceptables(await doc.house.schemaDom.tryPasteText(context, text, cache));
			}
		}
	}

	/**
	 * return null si aucun contenu valide détecté.
	 */
	extractDatasFromDragSession(dataTrsf: DataTransfer): OSkImportDatas {
		//TODO Cache du résultat (prop sur dataTrsf à tester)
		const doc = this.docHolder;
		if (doc && !this.readOnly) {
			if (this.config.dataTransferAnalyzer) {
				const r: OSkImportDatas = this.config.dataTransferAnalyzer.extractDatasFromDragSession(dataTrsf, this);
				if (r) return r.originalLinks || r.originalDom || r.originalHtml || r.originalText ? r : null;
			}
			const result: OSkImportDatas = {};
			if (dataTrsf.types.indexOf('application/xml') >= 0) {
				const content = DOM.parseDom(dataTrsf.getData('application/xml'));
				DOM.cleanupDom(content, true, false, true);
				result.originalDom = content.documentElement;
			}
			if (dataTrsf.types.indexOf('text/html') >= 0) {
				try {
					const docFrag = DOM.parseDomValid(dataTrsf.getData('text/html'), null, "text/html");
					if (docFrag) result.originalHtml = docFrag.body;
					// let docFrag = new Range().createContextualFragment(dataTrsf.getData('text/html'));
					// if (docFrag.hasChildNodes()) return {originalDom: docFrag};
				} catch (e) {}
			}
			if (dataTrsf.types.indexOf('text/plain') >= 0) {
				const text = dataTrsf.getData('text/plain');
				if (text) {
					result.originalText = text;
					if (!result.originalDom && !result.originalHtml) this.tryParseAsNode(text, result);
				}
			}
			if (result.originalDom || result.originalHtml || result.originalText) return result;
		}
		return null;
	}

	/** Parsing d'un contenu string. Choix de la méthode fixée en amont (pref ?) car sync nécessaire (dragSession). */
	tryParseAsNode(txt: string, result: OSkImportDatas, srcFormat?: 'text' | 'markdown') {
		if (!srcFormat && txt.startsWith('<')) {
			let doc = DOM.parseDomValid(txt, null, "application/xml");
			if (doc) {
				DOM.cleanupDom(doc, true, false, true);
				result.originalDom = doc.documentElement;
				return;
			}
			doc = DOM.parseDomValid(txt, null, "text/html");
			if (doc && doc.body.querySelector('*')) {
				//si on a au moins un elt (ie pas du texte pur dans le body).
				result.originalHtml = doc.body;
				return;
			}
		}
		try {
			if (srcFormat === 'text') {
				const lines = txt.split('\n');
				if (lines.length > 1) {
					const r = <section/>;
					for (let l of lines) if (l) r.appendChild(<p>{l}</p>);
					result.originalHtml = r;
				} else {
					result.originalHtml = <span>{txt}</span>;
				}
			} else {
				const doc = DOM.parseDomValid(markdownParse(txt), null, "text/html");
				if (doc) result.originalHtml = doc.body;
			}
		} catch (e) {}
	}

	/** Parsing d'un contenu string avec interrogation éventuelle du user. */
	async tryParseAsNodeAsync(txt: string, result: OSkImportDatas, srcFormat?: 'text' | 'markdown'): Promise<void> {
		//XXX popup choix format d'import... Html / Markdown / Text.
		this.tryParseAsNode(txt, result, srcFormat);
	}

	buildLastDatas(lastDatas: JLDWedEditor) {
		//FIXME Pour contenu lourd et OffView : mémoriser scroll par un xa proche du top + offsetY.
		const scrollCtn = this.wedEditor.scrollContainer;
		if (scrollCtn) lastDatas.scrollPos = scrollCtn.scrollTop;
		this.listeners.emit("buildLastDatas", this, lastDatas);
	}

	initLastDatas(lastDatas: JLDWedEditor) {
		if ('scrollPos' in lastDatas) {
			const scrollCtn = this.wedEditor.scrollContainer;
			if (this.rootWedlet) {
				//L'éditeur est déjà construit
				if (scrollCtn) scrollCtn.scrollTop = lastDatas.scrollPos;
				this.listeners.emit("initLastDatas", this, lastDatas);
			} else {
				//on attend le 1er dessinement de l'editeur.
				this.listeners.once("redrawAtEnd", () => {
					if (scrollCtn) scrollCtn.scrollTo({top: lastDatas.scrollPos, behavior: "auto"});
					this.listeners.emit("initLastDatas", this, lastDatas);
				});
			}
		}
	}
}

function containsDeleteMsg(msg: Msg): boolean {
	if (msg instanceof XmlDeleteMsg) return true;
	if (msg instanceof ListMsgOt && msg.msgs.findIndex(containsDeleteMsg) > 0) return true;
	return false;
}

interface JLDWedEditor extends JLastDatas {
	scrollPos?: number
}

export interface IWedMgrPointer {
	wedMgr?: WedMgr;
}

export function isWedMgrPointer(obj: any): obj is IWedMgrPointer {return obj && ('wedMgr' in obj)}

export interface OWedEditorConfig extends OLastDatasInit {
	reg?: IReg<IUiEnv>

	withoutScrolls?: boolean

	/**
	 * Autorise de sortir du contexte de l'éditeur par les touches de navigation (up, down...).
	 * Notamment pour des editeurs embarqués dans d'autres éditeurs.
	 */
	crossNav?: boolean
}

/**
 * Gestion de formats particuliers d'un DataTransfer (liens...)
 * en sus des mimeTypes standards (xml, HTML, text...).
 */
export interface IDataTransferWedAnalyzer {
	/**
	 * Gestion custom d'une action de type "Coller ici".
	 *
	 * @return Promise<null> si format non reconnu.
	 *    Promise<[]> si format reconnu mais pas de possibilité de collage trouvée, on bloque le collage d'une forme dégradée (texte).
	 */
	tryPaste(clipboardItems: ClipboardItem[], pasteCtx: OSkPasteContext, cache: OSkImportDatas, wedMgr: WedMgr): Promise<ISkImporter[] | null>

	/**
	 * Cas particulier du transfert de fichiers dans le clipboard : `dataTransfer.files` est spécifié.
	 */
	tryPasteFiles(trsfFiles: DataTransfer, pasteCtx: OSkPasteContext, cache: OSkImportDatas, wedMgr: WedMgr): Promise<ISkImporter[] | null>

	/**
	 * Extrait custom du DataTransfer les données pour gérer une insertion de données dans l'éditeur par drag and drop.
	 * @return
	 *   - null si aucune gestion custom (les tentatives d'extraction par défaut DOM et texte seront réalisés)
	 *   - {} pour signifier que le contenu de DataTransfer a été détecté, mais qu'aucun contenu est importable et qu'il ne
	 *        faut pas executer les tentatives d'extractions par défaut.
	 *   - {originalLinks:any} Si originalLinks renseigné l'insertion de liens sera prvilégiée,
	 *   - {originalDom:Node} Sinon, si originalDom renseigné l'insertion de Node sera prvilégiée,
	 *   - {originalText:string} Sinon, si originalText renseigné l'insertion sera recherchée.
	 */
	extractDatasFromDragSession(dataTrsf: DataTransfer, wedMgr: WedMgr): OSkImportDatas
}

/**
 * Editeur WED.
 *
 * Evènements générés:
 * - 'wed-editor-root-deleted' : En cas d'initialisation d'un fragment d'un document XML et d'une suppression concurrente de
 *        cette racine ou d'un ancêtre. L'env parent doit éliminer cet editor alors invalide.
 */
export interface IWedEditor extends IEltInitable, IRegPointer<IUiEnv>, IAccelKeyMgrPointer<IWedEditor> {

	/**
	 * Racine commune à tous les contenus de l'éditeur ET porteur du registre portant l'env graphique spcécialisé par le WedModel associé.
	 * Note :
	 * - Le IWedEditor référence un registre commun à ce container qui peut contenir d'autres sous-IWedEditor que le principal dans des bars (outils, preview)
	 * - Un sub reg est placé sur le rootNode et c'est ce dernier qui est obtenu via WedMgr.reg afin que d'autres WedModel puissent être
	 *  intégrés dans un même IWedEditor sans interférence de la cascade des noeuds: outline, preview...
	 */
	rootNode: HTMLElement & IRegPointer<IUiEnv & any>

	/** Container principal des wedlets. */
	mainContainer: HTMLElement

	/**
	 * Container principal des wedlets gérant le scroll principal de l'éditeur.
	 * null si pas de scroll, généralement mainContainer sinon.
	 * ATTENTION : utiliser findScrollCtn(from) pour trouver le container
	 * d'un elt particulier qui peut etre un autre scroll container : metaBar...
	 */
	scrollContainer?: HTMLElement | null

	wedMgr: WedMgr

	config: OWedEditorConfig

	initFromDocHolder(docHolder: IDocHolder, config?: OWedManagerConfig): Promise<boolean>

	onMsgError(errMsg: ErrorMsg): 'redraw' | void

	initLastDatasHooks: ((this: IWedEditor, lastDatas: JLastDatas) => void)[]
	buildLastDatasHooks: ((this: IWedEditor, lastDatas: JLastDatas) => void)[]

	/** INTERNAL ONLY utilisé pas WedMgr. */
	clearContent(): void
}

export function isWedEditor(elt: any): elt is IWedEditor {return elt && ("initFromDocHolder" in elt)}

/** Implémentation de base d'un éditor (pour embeding). */
export abstract class WedEditorBase extends HTMLElement implements IWedEditor, ILastDatasBuilder, IView, IEltInitable {

	config: OWedEditorConfig;
	reg: IReg<IUiEnv>;
	wedModel: WedModel;
	wedMgr: WedMgr;

	rootNode: HTMLElement & IRegPointer<any>;
	mainContainer: HTMLElement;
	scrollContainer?: HTMLElement;

	initLastDatasHooks: ((this: IWedEditor, lastDatas: JLastDatas) => void)[];
	buildLastDatasHooks: ((this: IWedEditor, lastDatas: JLastDatas) => void)[];

	get initialized(): boolean {return this.initialize == null}

	constructor() {
		super();
		this.wedMgr = new WedMgr(this);
	}

	initialize(init: OWedEditorConfig): this {
		this.initialize = null; //1 seul init dans la vie de l'objet.
		this.config = init;
		this.reg = REG.findReg(this, init); //Note si ce WedEditorBase === IWedEditor.rootNode, ce reg sera surchargé dans WedMgr.initWedMgr() en injectant le reg du wedModel.
		this.initUi(init);
		//LastDatas
		if (init.lastDatasKey) this.setAttribute("last-datas", init.lastDatasKey);
		if (init.lastDatas) {
			if (this.initLastDatasHooks) for (const hook of this.initLastDatasHooks) hook.call(this, init.lastDatas);
			this.wedMgr.initLastDatas(init.lastDatas);
		}
		return this;
	}

	buildInitFromAtts(init?: any): any {
		if (!init) init = {};
		(init as OWedEditorConfig).lastDatas = LASTDATAS.getLastDatas(this, this.getAttribute("last-datas"));
		return init;
	}

	protected initUi(init: OWedEditorConfig) {
		this.rootNode = this.mainContainer = this; //Version de base : rootNode est l'éditeur lui-même. Différent dans WedEditorBox par ex.
		if (!init.withoutScrolls) {
			this.scrollContainer = markAsScrollCtn(this.mainContainer);
			this.scrollContainer.style.overflow = "auto";
		}
		this.rootNode.addEventListener('delegate-host', (ev: Event) => {
			//cas d'editor dans un editor (diffrem par exemple), on doit bloquer.
			ev.stopPropagation();
		});
	}

	connectedCallback() {
		if (this.initialize) this.initialize(this.buildInitFromAtts());
		this.wedMgr.trackWindowChanges();
	}

	disconnectedCallback() {
		this.wedMgr.untrackWindowChanges();
	}

	setWedModel(wedModel: WedModel): this {
		this.wedModel = wedModel;
		return this;
	}

	initFromDocHolder(docHolder: IDocHolder, config?: OWedManagerConfig): Promise<boolean> {
		this.clearContent();
		if (!this.wedModel) throw Error("No wedModel declared");
		return this.wedMgr.initWedMgr(this.wedModel, docHolder, config);
	}

	clearContent() {
		this.rootNode.innerHTML = null;
	}

	/** En cas d'erreur, on laisse l'editeur principal prendre la main. */
	onMsgError(errMsg: ErrorMsg): 'redraw' | void {}

	buildLastDatas(parentLastDatas: JLastDatas) {
		const k = this.getAttribute("last-datas");
		if (k) {
			const lastDatas = parentLastDatas[k] = {};
			if (this.buildLastDatasHooks) for (const hook of this.buildLastDatasHooks) hook.call(this, lastDatas);
			this.wedMgr.buildLastDatas(lastDatas);
		}
	}

	onViewShown() {
		this.wedMgr.listeners.emit("shown", this.wedMgr);
	}

	onViewBeforeHide(close?: boolean): boolean {
		return this.wedMgr.listeners.emitUntil("beforeHide", this.wedMgr, close) !== false;
	}

	async onViewWaitForHide(close?: boolean): Promise<boolean> {
		return await this.wedMgr.listeners.emitAsyncUntil("beforeHideAsync", this.wedMgr, close) !== false;
	}

	onViewHidden(close?: boolean) {
		this.wedMgr.listeners.emit("hidden", this.wedMgr, close);
	}
}

/**
 * Affichage d'une preview de type tooltip, aucune de gestion de sélection etc.
 */
export class WedEditorMiniview extends WedEditorBase {

	constructor() {
		super();
		this.wedMgr.setReadOnly('WedEditorMiniview', true);
	}
}

customElements.define("wed-miniview", WedEditorMiniview);


/** Editor principal avec barres d'outils... */
export interface IWedEditorMain extends IWedEditor {

	initialize(config?: OWedEditorMainConfig): this

	barLayout: IWedEditorBarLayout
}

export interface OWedEditorMainConfig extends OWedEditorConfig {
	barLayout?: IWedEditorBarLayout
}

/** Gestionnaire du layout des bars de l'éditor. */
export interface IWedEditorBarLayout {

	initLayout(editor: IWedEditorMain, reg: IReg<IUiEnv>): void

	getBar(id: string): IWedEdBar

	/**
	 * Injecte cette bar dans le layout de l'éditeur si cette barre n'est pas déjà présente.
	 * Si cette bar est déjà dans l'arbre, la méthode s'assure juste que le layout s'ajuste considérant
	 * cette vue à présent visible (bordures...)
	 * Attention : le layout ne gère pas la propriété hidden de la bar elle-même.
	 * Retourne false si ce layout ne permet pas d'insérer cette bar.
	 */
	showBar(bar: IWedEdBar): boolean

	/**
	 * Eliminé la bar de l'arbre du DOM et ses éventuelles décorations propres au layout (panel) et
	 * ajuste son design (bordures...)
	 */
	removeBar(bar: IWedEdBar): void

	/**
	 * Si la bar s'est elle-même masquée via sa propriété hidden, cette méthode doit être appelée
	 * pour permettre au layout de s'ajuster graphiquement (bordures...)
	 * Attention : le layout ne gère pas la propriété hidden de la bar elle-même.
	 */
	onHideBar(bar: IWedEdBar): void
}


export interface WedPanelBar {
	initialize(init: OWedPanelBarInit): this
}

export interface OWedPanelBarInit extends OSkinableInit {
	barBody: IWedEdBar
	//TODO barActions?: OBarActionsInit<IWedMgrPointer>
}

export class WedPanelBar extends BaseElement implements IWedEdBar {

	//barActions: BarActions<IWedMgrPointer>;

	protected _initialize(init: OWedPanelBarInit) {
		const reg = this.findReg(init);
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);
		reg.installSkin('scroll/small', sr);
		// sr.appendChild(<div class="header"><span id="ti">{VIEWS.getLabel(init.barBody)}</span></div>);
		//TODO if (init.barActions) {}
		sr.appendChild(init.barBody);
	}
}

REG.reg.registerSkin('wed-panel-bar', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-height: 1.5em;
		min-width: 0;
		flex-direction: column;
	}

	#outlineBar {
		border-top: 1px solid var(--border-color);
	}

	#previewBar {
		flex: 1;
		background-color: var(--edit-bgcolor);
		color: var(--edit-color);
	}
`);
/*

	.header {
		display: flex;
		min-height: min-content;
		min-width: 0;
		background-color: var(--dialog-bgcolor);
		padding: 0.1em 0.3em;
	}

	#ti {
		flex: 1;
		user-select: none;
		color: var(--dialog-color);
	}
 */

customElements.define('wed-panel-bar', WedPanelBar);


/** */
export interface IIntersectHandler extends HTMLElement {
	onIntersectChange(entry: IntersectionObserverEntry): void
}


/** Panel ou barre associée à l'éditeur. */
export interface IWedEdBar extends IView {
	/** Id de la barre. Permet au IWedEditorBarLayout d'identifier la view et de la positionner correctement. */
	id: string
}

/**
 * Classe générique pour les actions WED en *écriture*.
 * Le contexte doit être un IWedMgrPointer c'est à dire
 * le IWedEditor, ou un IWedlet par exemple.
 */
export class WedEditAction<C extends IWedMgrPointer> extends Action<C> {

	//TODO ajout d'une perm générique de type "write"
	// constructor(id?: string) {
	// 	super(id);
	// 	this.requirePerm(...);
	// }

	isEnabled(ctx: C): boolean {
		if (ctx.wedMgr.readOnly) return false;
		return super.isEnabled(ctx);
	}

}

export class WedUndoAction extends WedEditAction<IWedMgrPointer> {
	_label = "Annuler";
	_icon = "/@skin@/edit/wed/undo.svg";
	_group = "undo";

	constructor(id: string = "wedUndo") {
		super(id);
	}

	isVisible(ctx: IWedMgrPointer): boolean {
		if (ctx.wedMgr.readOnly) return false;
		return super.isVisible(ctx);
	}

	execute(ctx: IWedMgrPointer, ev?: Event) {
		const wedMgr = ctx.wedMgr;
		wedMgr.docHolder.willUndo(wedMgr.config.undoRedoFilter).then((found: boolean) => {
			if (!found) POPUP.showNotifInfo("Aucune action à annuler", ctx.wedMgr.wedEditor);
		});
	}
}

export class WedRedoAction extends WedUndoAction {
	_label = "Refaire";
	_icon = "/@skin@/edit/wed/redo.svg";
	_group = "undo";

	constructor(id: string = "wedRedo") {
		super(id);
	}

	execute(ctx: IWedMgrPointer, ev?: Event) {
		const wedMgr = ctx.wedMgr;
		wedMgr.docHolder.willRedo(wedMgr.config.undoRedoFilter).then((found: boolean) => {
			if (!found) POPUP.showNotifInfo("Aucune action à refaire", ctx.wedMgr.wedEditor);
		});
	}
}

REG.reg.registerSvc("wedEditorUndoAction", 1, new WedUndoAction());
REG.reg.registerSvc("wedEditorRedoAction", 1, new WedRedoAction());

//REG.reg.addSvcToList("actions:wed:global", "undo", 1, "wedEditorUndoAction"); ergonomiquement très discutable : l'action ne correspond pas à l'objet sélectionné.
//REG.reg.addSvcToList("actions:wed:global", "redo", 1, "wedEditorRedoAction"); ergonomiquement très discutable : l'action ne correspond pas à l'objet sélectionné.

REG.reg.addSvcToList("accelKeys:wed:global", "Z-accel", 1, "wedEditorUndoAction");
REG.reg.addSvcToList("accelKeys:wed:global", "Y-accel", 1, "wedEditorRedoAction");

function onFocus(this: IWedMgrPointer, ev: FocusEvent) {
	//On bloque une réentrance lié au delegatedHost.
	const wedMgr = this.wedMgr;
	if (wedMgr._lastFocusEvent === ev) return;
	wedMgr._lastFocusEvent = ev;
	const composedPath = ev.composedPath();
	let focusElt: Element;
	for (let i = 0; i < composedPath.length; i++) {
		const o = composedPath[i];
		if (o instanceof Element) {
			focusElt = o;
			break;
		}
	}
	wedMgr.lastFocus = focusElt;
	//console.trace("focusElt:::::", focusElt, this);
	if (focusElt) wedMgr.listeners.emitCatched("getFocus", wedMgr, focusElt);
}

function onBlur(this: IWedMgrPointer, ev: FocusEvent) {
	//On bloque une réentrance lié au delegatedHost.
	const wedMgr = this.wedMgr;
	if (wedMgr._lastFocusEvent === ev) return;
	wedMgr._lastFocusEvent = ev;
	wedMgr.listeners.emitCatched("looseFocus", wedMgr, ev);
}

/**
 * Compat du module d'édition : exige
 * - l'implémentation de ShadowRoot.prototype.getSelection()
 * - Customized built-in elements (ie qui héritent d'autres élements que HTMLElement).
 */
Desk.compatTable.set('edit', (): boolean => {
	if (!ShadowRoot.prototype.getSelection) return false;
	return Desk.navVersions["Chrome"] >= 67; //TODO trouver une solution pour détecter la feature 'Customized built-in elements'.
	//if (!('ResizeObserver' in window)) return false;
});
